import { createSlice } from "@reduxjs/toolkit";
import {
  Subscription,
  User,
  createSubscriptionAction,
  getSubscriptionByUserId,
  updateSubscriptionAction,
  cancelSubscriptionAction,
  confirmSubscriptionPaymentAction,
  getSubscriptionDetailsAction,
  SubscriptionDetailsResponseData,
  getBillingDetailsByUserId,
  BillingDetails,
  getBillingHistoryAction,
  getBillingHistoryByUserId,
  BillingHistory,
  getUserInvoicesListAction,
  Invoice,
  downloadInvoiceAction,
  ScheduledSubscriptionDetails,
  ScheduledPlanDetails,
  getScheduledPlanAction,
  getFinancialMetricsAction,
  FinancialMetricsResponse,
  getRecurringRevenueAction,
  RecurringRevenueResponse,
} from "./subscriptionAction";

interface SubscriptionState {
  subscription: Subscription | null;
  user: User | null;
  subscriptionDetails: SubscriptionDetailsResponseData | null;
  billingDetails: BillingDetails[] | null;
  billingHistory: BillingHistory[] | null;
  invoices: Invoice[] | null;
  invoicePdfUrl: string | null;
  loading: boolean;
  error: string | null;
  updateSubscriptionLoading: boolean;
  updateSubscriptionError: string | null;
  billingDetailLoading: boolean;
  billingDetailError: string | null;
  billingHistoryLoading: boolean;
  billingHistoryError: string | null;
  invoicesLoading: boolean;
  invoicesError: string | null;
  invoiceLoading: boolean;
  invoiceError: string | null;
  scheduledPlan: {
    subscriptionDetails: ScheduledSubscriptionDetails;
    scheduledPlanDetails: ScheduledPlanDetails;
  } | null;
  scheduledPlanLoading: boolean;
  scheduledPlanError: string | null;
  cancelSubscriptionLoading: boolean;
  cancelSubscriptionError: string | null;
  financialMetrics: FinancialMetricsResponse | null;
  financialMetricsLoading: boolean;
  financialMetricsError: string | null;
  recurringRevenue: RecurringRevenueResponse | null;
  recurringRevenueLoading: boolean;
  recurringRevenueError: string | null;
}

const initialState: SubscriptionState = {
  subscription: null,
  user: null,
  subscriptionDetails: null,
  billingDetails: null,
  billingHistory: null,
  invoices: null,
  invoicePdfUrl: null,
  loading: false,
  error: null,
  updateSubscriptionLoading: false,
  updateSubscriptionError: null,
  billingDetailLoading: false,
  billingDetailError: null,
  billingHistoryLoading: false,
  billingHistoryError: null,
  invoicesLoading: false,
  invoicesError: null,
  invoiceLoading: false,
  invoiceError: null,
  scheduledPlan: null,
  scheduledPlanLoading: false,
  scheduledPlanError: null,
  cancelSubscriptionLoading: false,
  cancelSubscriptionError: null,
  financialMetrics: null,
  financialMetricsLoading: false,
  financialMetricsError: null,
  recurringRevenue: null,
  recurringRevenueLoading: false,
  recurringRevenueError: null,
};

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    clearSubscriptionMessages(state) {
      state.error = null;
      state.updateSubscriptionError = null;
      state.billingDetailError = null;
      state.billingHistoryError = null;
      state.invoicesError = null;
      state.invoiceError = null;
      state.scheduledPlanError = null;
      state.cancelSubscriptionError = null;
      state.financialMetricsError = null;
      state.recurringRevenueError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE SUBSCRIPTION
      .addCase(createSubscriptionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscriptionAction.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data?.subscription) {
          state.subscription = action.payload.data.subscription;
        }
        state.error = null;
      })
      .addCase(createSubscriptionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create subscription";
      })

      // GET SUBSCRIPTION
      .addCase(getSubscriptionByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscriptionByUserId.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          const data = action.payload.data;
          state.user = {
            id: data.userId,
            email: data.email,
            firstname: data.firstname,
            lastname: data.lastname,
          };
          state.subscription = {
            id: data.subscriptionId,
            planId: data.planId,
            siteQuotaLimit: data.subscriptionDetails.siteQuotaLimit,
            siteQuotaUsed: data.subscriptionDetails.siteQuotaUsed,
            fleetQuotaLimit: data.subscriptionDetails.fleetQuotaLimit,
            fleetQuotaUsed: data.subscriptionDetails.fleetQuotaUsed,
            startDate: data.subscriptionDetails.startDate,
            endDate: data.subscriptionDetails.endDate,
            status: data.subscriptionDetails.status,
            stripeCustomerId: data.subscriptionDetails.stripeCustomerId,
            stripeSubscriptionId: data.subscriptionDetails.stripeSubscriptionId,
            stripeScheduleId: data.subscriptionDetails.stripeScheduleId,
          };
        } else {
          state.subscription = null;
          state.user = null;
        }
        state.error = null;
      })
      .addCase(getSubscriptionByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch subscription";
      })

      // GET SUBSCRIPTION DETAILS
      .addCase(getSubscriptionDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubscriptionDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionDetails = action.payload.data || null;
        state.error = null;
      })
      .addCase(getSubscriptionDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch subscription details";
      })

      // UPDATE SUBSCRIPTION
      .addCase(updateSubscriptionAction.pending, (state) => {
        state.updateSubscriptionLoading = true;
        state.updateSubscriptionError = null;
      })
      .addCase(updateSubscriptionAction.fulfilled, (state, action) => {
        state.updateSubscriptionLoading = false;
        state.subscription = action.payload.data || state.subscription;
        state.updateSubscriptionError = null;
      })
      .addCase(updateSubscriptionAction.rejected, (state, action) => {
        state.updateSubscriptionLoading = false;
        state.updateSubscriptionError =
          action.payload || "Failed to update subscription";
      })

      // CANCEL SUBSCRIPTION
      .addCase(cancelSubscriptionAction.pending, (state) => {
        state.cancelSubscriptionLoading = true;
        state.cancelSubscriptionError = null;
      })
      .addCase(cancelSubscriptionAction.fulfilled, (state, action) => {
        state.cancelSubscriptionLoading = false;
        if (state.subscription && action.payload.data?.cancelAt) {
          state.subscription.endDate = action.payload.data.cancelAt;
        }
        state.cancelSubscriptionError = null;
      })
      .addCase(cancelSubscriptionAction.rejected, (state, action) => {
        state.cancelSubscriptionLoading = false;
        state.cancelSubscriptionError =
          action.payload || "Failed to cancel subscription";
      })

      // CONFIRM SUBSCRIPTION PAYMENT
      .addCase(confirmSubscriptionPaymentAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmSubscriptionPaymentAction.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(confirmSubscriptionPaymentAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to confirm payment";
      })

      // GET BILLING DETAILS
      .addCase(getBillingDetailsByUserId.pending, (state) => {
        state.billingDetailLoading = true;
        state.billingDetailError = null;
      })
      .addCase(getBillingDetailsByUserId.fulfilled, (state, action) => {
        state.billingDetailLoading = false;
        state.billingDetails = action.payload.data || null;
        state.billingDetailError = null;
      })
      .addCase(getBillingDetailsByUserId.rejected, (state, action) => {
        state.billingDetailLoading = false;
        state.billingDetailError =
          action.payload || "Failed to fetch billing details";
      })

      // GET BILLING HISTORY
      .addCase(getBillingHistoryAction.pending, (state) => {
        state.billingHistoryLoading = true;
        state.billingHistoryError = null;
      })
      .addCase(getBillingHistoryAction.fulfilled, (state, action) => {
        state.billingHistoryLoading = false;
        state.billingHistory = action.payload.data || null;
        state.billingHistoryError = null;
      })
      .addCase(getBillingHistoryAction.rejected, (state, action) => {
        state.billingHistoryLoading = false;
        state.billingHistoryError =
          action.payload || "Failed to fetch billing history";
      })

      // NEW: GET BILLING HISTORY BY USER ID
      .addCase(getBillingHistoryByUserId.pending, (state) => {
        state.billingHistoryLoading = true;
        state.billingHistoryError = null;
      })
      .addCase(getBillingHistoryByUserId.fulfilled, (state, action) => {
        state.billingHistoryLoading = false;
        state.billingHistory = action.payload.data || null;
        state.billingHistoryError = null;
      })
      .addCase(getBillingHistoryByUserId.rejected, (state, action) => {
        state.billingHistoryLoading = false;
        state.billingHistoryError =
          action.payload || "Failed to fetch billing history";
      })

      // GET USER INVOICES LIST
      .addCase(getUserInvoicesListAction.pending, (state) => {
        state.invoicesLoading = true;
        state.invoicesError = null;
      })
      .addCase(getUserInvoicesListAction.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.invoices = action.payload.data || null;
        state.invoicesError = null;
      })
      .addCase(getUserInvoicesListAction.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.invoicesError = action.payload || "Failed to fetch invoices list";
      })

      // DOWNLOAD INVOICE
      .addCase(downloadInvoiceAction.pending, (state) => {
        state.invoiceLoading = true;
        state.invoiceError = null;
      })
      .addCase(downloadInvoiceAction.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        state.invoicePdfUrl = action.payload.data?.pdfUrl || null;
        state.invoiceError = null;
      })
      .addCase(downloadInvoiceAction.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.invoiceError =
          action.payload || "Failed to fetch invoice PDF URL";
      })

      // GET UPCOMING SCHEDULE PLAN
      .addCase(getScheduledPlanAction.pending, (state) => {
        state.scheduledPlanLoading = true;
        state.scheduledPlanError = null;
      })
      .addCase(getScheduledPlanAction.fulfilled, (state, action) => {
        state.scheduledPlanLoading = false;
        state.scheduledPlan = action.payload.data || null;
        state.scheduledPlanError = null;
      })
      .addCase(getScheduledPlanAction.rejected, (state, action) => {
        state.scheduledPlanLoading = false;
        state.scheduledPlanError =
          action.payload || "Failed to fetch scheduled plan";
      })

      // NEW: GET FINANCIAL METRICS
      .addCase(getFinancialMetricsAction.pending, (state) => {
        state.financialMetricsLoading = true;
        state.financialMetricsError = null;
      })
      .addCase(getFinancialMetricsAction.fulfilled, (state, action) => {
        state.financialMetricsLoading = false;
        state.financialMetrics = action.payload.data || null;
        state.financialMetricsError = null;
      })
      .addCase(getFinancialMetricsAction.rejected, (state, action) => {
        state.financialMetricsLoading = false;
        state.financialMetricsError =
          action.payload || "Failed to fetch financial metrics";
      })

      // NEW: GET RECURRING REVENUE
      .addCase(getRecurringRevenueAction.pending, (state) => {
        state.recurringRevenueLoading = true;
        state.recurringRevenueError = null;
      })
      .addCase(getRecurringRevenueAction.fulfilled, (state, action) => {
        state.recurringRevenueLoading = false;
        state.recurringRevenue = action.payload.data || null;
        state.recurringRevenueError = null;
      })
      .addCase(getRecurringRevenueAction.rejected, (state, action) => {
        state.recurringRevenueLoading = false;
        state.recurringRevenueError =
          action.payload || "Failed to fetch recurring revenue";
      });
  },
});

export const { clearSubscriptionMessages } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;